import { createMuiTheme } from '@material-ui/core/styles';

export const GLASS = {
  background: 'rgba(255,255,255,0.75)',
  backgroundHover: 'rgba(255,255,255,0.90)',
  border: '1px solid rgba(0,0,0,0.07)',
  borderAccent: '1px solid rgba(59,130,246,0.3)',
  blur: 'blur(20px)',
  shadow: '0 4px 16px rgba(0,0,0,0.08)',
  shadowElevated: '0 8px 32px rgba(0,0,0,0.12)',
};

// NOTA: os valores NEUTROS (fundos/texto/borda) estão envoltos em
// `var(--adm-*, <original>)`. Os tokens --adm-* só existem no BACKOFFICE em modo
// Claro/Escuro (data-admin-theme), por isso no PLANNER e no backoffice CLÁSSICO
// usa-se sempre o fallback → visual IDÊNTICO; só o Claro/Escuro do admin troca.
// Cores de MARCA/semânticas (accent/success/warning/error/gradient) ficam fixas.
export const COLORS = {
  bg: 'var(--adm-bg, #f8fafc)',
  bgSecondary: 'var(--adm-card-hover, #f1f5f9)',
  bgCard: 'var(--adm-card, #ffffff)',
  accent: '#3b82f6',
  accentDark: '#2563eb',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  text: 'var(--adm-text, #0f172a)',
  textSecondary: 'var(--adm-text-dim, #475569)',
  textMuted: 'var(--adm-text-muted, #94a3b8)',
  border: 'var(--adm-card-border, rgba(0,0,0,0.07))',
  gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
};

const theme = createMuiTheme({
  palette: {
    type: 'light',
    primary: {
      main: '#3b82f6',
      dark: '#2563eb',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6366f1',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
    },
    success: { main: '#22c55e' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
    h1: { fontWeight: 300, letterSpacing: '-0.02em' },
    h2: { fontWeight: 300, letterSpacing: '-0.01em' },
    h3: { fontWeight: 400 },
    h4: { fontWeight: 600, letterSpacing: '0.02em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.75rem' },
    subtitle1: { fontWeight: 400, color: '#475569' },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 300, color: '#475569' },
    caption: { fontWeight: 400, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.65rem', color: '#94a3b8' },
  },
  shape: { borderRadius: 8 },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '*': { scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' },
        '*::-webkit-scrollbar': { width: '4px' },
        '*::-webkit-scrollbar-track': { background: '#f1f5f9' },
        '*::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '2px' },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: '0.02em',
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
        color: '#ffffff',
        boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #60a5fa 0%, #818cf8 100%)',
          boxShadow: '0 6px 16px rgba(59,130,246,0.4)',
        },
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        minWidth: 80,
        color: '#64748b',
      },
    },
    MuiTextField: {
      root: {},
    },
    MuiOutlinedInput: {
      root: {
        '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
        '&:hover fieldset': { borderColor: 'rgba(59,130,246,0.5)' },
        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
        background: '#ffffff',
      },
      input: { color: '#0f172a' },
    },
    MuiInputLabel: {
      root: { color: '#64748b' },
    },
    MuiSelect: {
      icon: { color: '#64748b' },
    },
    MuiMenuItem: {
      root: {
        '&:hover': { background: 'rgba(59,130,246,0.06)' },
        '&.Mui-selected': { background: 'rgba(59,130,246,0.10)' },
      },
    },
    MuiChip: {
      root: {
        borderRadius: 4,
        height: 24,
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
      },
    },
    MuiDivider: {
      root: { backgroundColor: 'rgba(0,0,0,0.07)' },
    },
    MuiTooltip: {
      tooltip: {
        background: '#1e293b',
        border: '1px solid rgba(0,0,0,0.1)',
        fontSize: '0.75rem',
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: '#ffffff',
      },
    },
  },
});

export default theme;

// ─── Tema "Moderno" (linguagem visual FuelCheck) ──────────────────────────
// Opt-in por perfil (clientSettings.uiTheme === 'modern'). Mantém a MESMA
// estrutura de componentes; só muda os tokens visuais (paleta clara e arejada,
// azul limpo, cartões arredondados com borda+sombra subtil, botões sólidos sem
// gradiente, chips em pílula, fonte Geist/Inter). Quem não ativa fica no clássico.
// Primitivos EXATOS do FuelCheck (DevTools :root).
export const MODERN_COLORS = {
  bg0: '#e9eff5', bg1: '#f5f8fb', bg2: '#ffffff', bg3: '#eef3f8', bg4: '#dceaf5',
  bg: '#f5f8fb',          // --bg-1 (fundo da página)
  bgSecondary: '#eef3f8', // --bg-3
  bgCard: '#ffffff',      // --bg-2
  accent: '#0677a6',      // --accent
  cyan: '#0796cc',        // --cyan (brilhos)
  sky: '#2e95cb',         // --sky
  mint: '#10a774',        // --mint
  green: '#5e9a54',       // --green
  // Estados (base)
  stOk: '#0fa06c', stDiv: '#d17d00', stFora: '#e02a4f', stExc: '#7f52e8', stNd: '#6a7a90',
  // Estados (texto, mais escuro)
  stOkTx: '#0a7a52', stDivTx: '#a35f00', stForaTx: '#c21f42', stExcTx: '#6a3fd0',
  success: '#0fa06c', warning: '#d17d00', error: '#e02a4f', purple: '#7f52e8',
  // Texto
  ink: '#0b1a2c', text: '#0b1a2c', text2: '#3a4e66', textSecondary: '#5e7189', textMuted: '#9eadbf',
  // Linhas (rgba 22,58,96)
  line1: 'rgba(22,58,96,0.07)', line2: 'rgba(22,58,96,0.12)', line3: 'rgba(22,58,96,0.2)',
  border: 'rgba(22,58,96,0.12)',
  scrollbar: '#c3d1df',
  // Raios
  r1: 6, r2: 10, r3: 14, r4: 20, rPill: 999,
  // Sombras
  shadow1: '0 1px 2px rgba(20,42,70,.08), 0 2px 8px rgba(20,42,70,.06)',
  shadow2: '0 8px 28px rgba(20,42,70,.1), 0 2px 6px rgba(20,42,70,.06)',
  shadow3: '0 24px 64px rgba(20,42,70,.16), 0 4px 12px rgba(20,42,70,.08)',
  brandGrad: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
};

export const modernTheme = createMuiTheme({
  palette: {
    type: 'light',
    primary: { main: '#0677a6', dark: '#055a80', contrastText: '#ffffff' },
    secondary: { main: '#10a774' },
    background: { default: '#f5f8fb', paper: '#ffffff' },
    text: { primary: '#0b1a2c', secondary: '#5e7189' },
    success: { main: '#0fa06c' },
    warning: { main: '#d17d00' },
    error: { main: '#e02a4f' },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontWeight: 600, letterSpacing: '-0.015em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.72rem' },
    subtitle1: { fontWeight: 500, color: '#5b6b7b' },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400, color: '#5b6b7b' },
    button: { fontWeight: 600 },
    caption: { fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.64rem', color: '#9eadbf' },
  },
  shape: { borderRadius: 10 },  // --r-2
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: { backgroundColor: '#f5f8fb' },  // --bg-1
        '*': { scrollbarWidth: 'thin', scrollbarColor: '#c3d1df #eef3f8' },
        '*::-webkit-scrollbar': { width: '8px', height: '8px' },
        '*::-webkit-scrollbar-track': { background: '#eef3f8' },
        '*::-webkit-scrollbar-thumb': { background: '#c3d1df', borderRadius: '6px' },
      },
    },
    MuiButton: {
      root: { textTransform: 'none', fontWeight: 600, borderRadius: 10, letterSpacing: 0 },
      // Botão primário com o GRADIENTE da marca ecooGPS (eco-btn--primary do UI kit).
      containedPrimary: {
        background: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)',
        color: '#03121c',
        boxShadow: '0 0 0 1px rgba(11,26,44,0.1), 0 6px 24px rgba(7,150,204,0.35)',
        '&:hover': { background: 'linear-gradient(90deg, #58BAE6 0%, #18BDEE 45%, #2FD596 100%)', filter: 'brightness(1.06)', boxShadow: '0 0 0 1px rgba(11,26,44,0.16), 0 8px 30px rgba(7,150,204,0.5)' },
      },
      outlined: { borderColor: 'rgba(22,58,96,0.12)' },
    },
    MuiPaper: {
      root: { backgroundColor: '#ffffff' },
      rounded: { borderRadius: 14 },  // --r-3
      elevation1: { boxShadow: '0 1px 2px rgba(20,42,70,.08), 0 2px 8px rgba(20,42,70,.06)', border: '1px solid rgba(22,58,96,0.07)' },  // --shadow-1 + --line-1
      elevation2: { boxShadow: '0 8px 28px rgba(20,42,70,.1), 0 2px 6px rgba(20,42,70,.06)', border: '1px solid rgba(22,58,96,0.07)' },  // --shadow-2
    },
    MuiDialog: {
      paper: { borderRadius: 20, border: '1px solid rgba(22,58,96,0.07)', boxShadow: '0 24px 64px rgba(20,42,70,.16), 0 4px 12px rgba(20,42,70,.08)' },  // --r-4 + --shadow-3
    },
    MuiTab: { root: { textTransform: 'none', fontWeight: 600, minWidth: 80, color: '#5e7189' } },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
        background: '#ffffff',
        '& fieldset': { borderColor: 'rgba(22,58,96,0.12)' },  // --line-2
        '&:hover fieldset': { borderColor: 'rgba(22,58,96,0.2)' },  // --line-3
        '&.Mui-focused fieldset': { borderColor: '#0677a6', borderWidth: 1.5 },
      },
      input: { color: '#0b1a2c' },
    },
    MuiInputLabel: { root: { color: '#5e7189' } },
    MuiSelect: { icon: { color: '#5e7189' } },
    MuiMenuItem: {
      root: {
        '&:hover': { background: 'rgba(6,119,166,0.06)' },
        '&.Mui-selected': { background: 'rgba(6,119,166,0.10)' },
      },
    },
    MuiChip: {
      root: { borderRadius: 999, height: 22, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' },  // --r-pill
      outlined: { borderColor: 'rgba(22,58,96,0.12)' },
    },
    MuiDivider: { root: { backgroundColor: 'rgba(22,58,96,0.07)' } },
    MuiTooltip: {
      tooltip: { background: '#0b1a2c', borderRadius: 8, fontSize: '0.74rem', fontWeight: 500, padding: '6px 10px' },
    },
  },
});

// ─── Tema FuelCheck ESCURO (modo escuro do backoffice) ─────────────────────
// palette.type:'dark' → todos os componentes MUI (Dialog, TextField, Select,
// Table, Chip, Paper, Menu) passam a superfícies escuras + texto claro
// automaticamente. Paleta inspirada no fuelcheck.ecoogps.com em modo escuro:
// navy profundo, acento ciano/teal, verde "conforme".
const DK = {
  bg: '#0b1726', paper: '#14273d', input: '#0f2033', ink: '#e7eef6', tx3: '#93a7be',
  accent: '#20a6d4', line1: 'rgba(146,175,205,0.12)', line2: 'rgba(146,175,205,0.20)', line3: 'rgba(146,175,205,0.30)',
};
export const modernDarkTheme = createMuiTheme({
  palette: {
    type: 'dark',
    primary: { main: DK.accent, dark: '#1485ad', contrastText: '#06121f' },
    secondary: { main: '#22c08a' },
    background: { default: DK.bg, paper: DK.paper },
    text: { primary: DK.ink, secondary: DK.tx3 },
    success: { main: '#22c08a' }, warning: { main: '#d8a23a' }, error: { main: '#e85d7a' },
    divider: DK.line1,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 600, letterSpacing: '-0.02em' },
    h2: { fontWeight: 600, letterSpacing: '-0.015em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700 }, h5: { fontWeight: 700 },
    h6: { fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.72rem' },
    subtitle1: { fontWeight: 500, color: DK.tx3 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400, color: DK.tx3 },
    button: { fontWeight: 600 },
    caption: { fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.64rem', color: DK.tx3 },
  },
  shape: { borderRadius: 10 },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: { backgroundColor: DK.bg },
        '*': { scrollbarWidth: 'thin', scrollbarColor: '#2a3f59 #0f1f33' },
        '*::-webkit-scrollbar': { width: '8px', height: '8px' },
        '*::-webkit-scrollbar-track': { background: '#0f1f33' },
        '*::-webkit-scrollbar-thumb': { background: '#2a3f59', borderRadius: '6px' },
      },
    },
    MuiButton: {
      root: { textTransform: 'none', fontWeight: 600, borderRadius: 10, letterSpacing: 0 },
      // Botão primário com o GRADIENTE da marca ecooGPS (variante escura do UI kit).
      containedPrimary: {
        background: 'linear-gradient(90deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)', color: '#03121c',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.1), 0 6px 24px rgba(32,198,244,0.35)',
        '&:hover': { background: 'linear-gradient(90deg, #6EC1E4 0%, #20C6F4 45%, #3DDC97 100%)', filter: 'brightness(1.06)', boxShadow: '0 0 0 1px rgba(255,255,255,0.16), 0 8px 30px rgba(32,198,244,0.5)' },
      },
      outlined: { borderColor: DK.line2, color: DK.ink },
    },
    MuiPaper: {
      root: { backgroundColor: DK.paper },
      rounded: { borderRadius: 14 },
      elevation1: { boxShadow: '0 1px 2px rgba(0,0,0,0.4)', border: `1px solid ${DK.line1}`, backgroundColor: DK.paper },
      elevation2: { boxShadow: '0 8px 28px rgba(0,0,0,0.5)', border: `1px solid ${DK.line1}`, backgroundColor: DK.paper },
    },
    MuiDialog: {
      paper: { borderRadius: 20, backgroundColor: DK.paper, border: `1px solid ${DK.line2}`, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' },
    },
    MuiTab: { root: { textTransform: 'none', fontWeight: 600, minWidth: 80, color: DK.tx3 } },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10, background: DK.input,
        '& fieldset': { borderColor: DK.line2 },
        '&:hover fieldset': { borderColor: DK.line3 },
        '&.Mui-focused fieldset': { borderColor: DK.accent, borderWidth: 1.5 },
      },
      input: { color: DK.ink },
    },
    MuiInputLabel: { root: { color: DK.tx3 } },
    MuiSelect: { icon: { color: DK.tx3 } },
    MuiMenuItem: {
      root: {
        '&:hover': { background: 'rgba(32,166,212,0.12)' },
        '&.Mui-selected': { background: 'rgba(32,166,212,0.18)' },
      },
    },
    MuiChip: {
      root: { borderRadius: 999, height: 22, fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' },
      outlined: { borderColor: DK.line2 },
    },
    MuiDivider: { root: { backgroundColor: DK.line1 } },
    MuiTooltip: {
      tooltip: { background: '#06121f', borderRadius: 8, fontSize: '0.74rem', fontWeight: 500, padding: '6px 10px', border: `1px solid ${DK.line2}` },
    },
  },
});
